/**
 * Hub de ajustes: conteos por reporte y acceso a la ficha.
 */
import React, { useMemo, useState } from 'react';
import { ink, studio, SearchField } from './studioUi';

const countsOf = (report) => report.adjustmentCounts || { pending: 0, completed: 0, validated: 0, total: 0 };

const ReportAdjustmentsHub = ({ dashboards = [], onOpenReport }) => {
    const [query, setQuery] = useState('');
    const [onlyPending, setOnlyPending] = useState(true);

    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (dashboards || [])
            .map((report) => ({ report, counts: countsOf(report) }))
            .filter(({ report, counts }) => {
                if (onlyPending && counts.pending < 1 && counts.completed < 1) return false;
                if (!q) return true;
                const hay = [report.reporteNombre, report.cliente, report.proyecto].join(' ').toLowerCase();
                return hay.includes(q);
            })
            .sort((a, b) => (b.counts.pending - a.counts.pending) || (b.counts.completed - a.counts.completed));
    }, [dashboards, onlyPending, query]);

    const totals = useMemo(() => rows.reduce((acc, row) => ({
        pending: acc.pending + row.counts.pending,
        completed: acc.completed + row.counts.completed,
        validated: acc.validated + row.counts.validated,
    }), { pending: 0, completed: 0, validated: 0 }), [rows]);

    return (
        <div style={studio.page}>
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Administración</p>
                    <h2 style={studio.title}>Ajustes de reportes</h2>
                    <p style={studio.subtitle}>Pendientes y por validar en cada reporte. Abre la ficha para crear, completar o validar.</p>
                </div>
                <div style={studio.stats}>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{totals.pending}</span>
                        <span style={studio.statLabel}>Pendientes</span>
                    </div>
                    <div style={{ ...studio.stat, borderLeftColor: ink.accent }}>
                        <span style={studio.statValue}>{totals.completed}</span>
                        <span style={studio.statLabel}>Por validar</span>
                    </div>
                    <div style={{ ...studio.stat, borderLeftColor: '#137333' }}>
                        <span style={studio.statValue}>{totals.validated}</span>
                        <span style={studio.statLabel}>Validados</span>
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                    <SearchField value={query} onChange={setQuery} placeholder="Buscar reporte, cliente o proyecto" />
                </div>
                <button type="button" style={onlyPending ? studio.primaryBtn : studio.ghostBtn} onClick={() => setOnlyPending(true)}>Con trabajo abierto</button>
                <button type="button" style={!onlyPending ? studio.primaryBtn : studio.ghostBtn} onClick={() => setOnlyPending(false)}>Todos</button>
            </div>

            {rows.length === 0 ? (
                <div style={studio.empty}>No hay reportes en este filtro.</div>
            ) : (
                <div style={studio.card}>
                    <table style={studio.table}>
                        <thead>
                            <tr>
                                <th style={studio.th}>Reporte</th>
                                <th style={studio.th}>Cliente</th>
                                <th style={studio.th}>Pendientes</th>
                                <th style={studio.th}>Por validar</th>
                                <th style={studio.th}>Validados</th>
                                <th style={studio.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(({ report, counts }) => (
                                <tr key={report.id}>
                                    <td style={studio.td}>{report.reporteNombre}</td>
                                    <td style={studio.td}>{report.cliente || '—'}</td>
                                    <td style={studio.td}>{counts.pending}</td>
                                    <td style={studio.td}>{counts.completed}</td>
                                    <td style={studio.td}>{counts.validated}</td>
                                    <td style={studio.td}>
                                        <button type="button" style={studio.ghostBtn} onClick={() => onOpenReport(report)}>Abrir ficha</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ReportAdjustmentsHub;
